import type { TFunction } from 'i18next'
import * as Yup from 'yup'
import type { Address } from '@dhl/input-address'
import { createCompanyNameValidationSchema, createVatNumberValidationSchema } from '@dhl/inputs'
import {
  type BusinessUnitCode,
  isBeNlBusinessUnit,
  isCccBusinessUnit,
  isPplCzBusinessUnit,
} from '~/common/model/businessunits'
import type { LabelSettings } from '~/modules/organisations/model/OrganisationData'

const labelPrefix = 'admin.organisationsPage.organisationFormInputErrorMessage'

const createContactSchema = (t: TFunction) =>
  Yup.object().shape({
    firstName: Yup.string()
      .default('')
      .required(t(`${labelPrefix}.firstNameIsRequired`)),
    lastName: Yup.string()
      .default('')
      .required(t(`${labelPrefix}.lastNameIsRequired`)),
    email: Yup.string()
      .default('')
      .email(t(`${labelPrefix}.emailAddressIsRequired`)),
    phone: Yup.string()
      .default('')
      .matches(/^[\d\s()+-]{9,15}$/, t(`${labelPrefix}.phoneIsRequired`))
      .required(t(`${labelPrefix}.phoneIsRequired`)),
  })

const createVatSchema = (t: TFunction) =>
  createVatNumberValidationSchema(t)
    .required(t('inputs.vatNumberInput.vatNumberIsRequiredErrorMessage'))
    .min(11, t('inputs.vatNumberInput.vatNumberIsShortMessage', { min: 11 }))

const createIcoSchema = (t: TFunction) =>
  Yup.string()
    .default('')
    .trim()
    .required(t('inputs.icoNumberInput.icoNumberIsRequiredErrorMessage'))
    .matches(/^\d+$/, t('inputs.icoNumberInput.icoNumberInvalidFormatMessage'))
    .min(8, t('inputs.icoNumberInput.icoNumberIsShortMessage', { min: 8 }))
    .max(8, t('inputs.icoNumberInput.icoNumberIsLongMessage', { max: 8 }))

const createBaseDataSchema = (t: TFunction, addressValidationSchema: Yup.Schema<Address>) => ({
  address: addressValidationSchema,
  contactPerson: createContactSchema(t),
  vatNumber: createVatSchema(t),
})

const createLabelSettingsSchema = () =>
  Yup.object().shape({
    undeliverableReturnPostboxNumber: Yup.string(),
  })

const createOptionalLabelSchema = () =>
  Yup.lazy((labelSettings: LabelSettings | null | undefined) =>
    !labelSettings ? Yup.mixed().notRequired() : createLabelSettingsSchema()
  )

const createCccSchema = (t: TFunction) =>
  Yup.object().shape({
    customerId: Yup.string()
      .default('')
      .required(t(`${labelPrefix}.customerIdIsRequired`))
      .min(1, t(`${labelPrefix}.customerIdMin`))
      .max(35, t(`${labelPrefix}.customerIdMax`)),
    billingUnitId: Yup.string()
      .default('')
      .required(t(`${labelPrefix}.billingUnitIdIsRequired`)),
  })

const createDefaultDataSchema = (t: TFunction, addressValidationSchema: Yup.Schema<Address>) =>
  Yup.object().shape({
    ...createBaseDataSchema(t, addressValidationSchema),
    chamberOfCommerce: Yup.string()
      .default('')
      .required(t(`${labelPrefix}.COCNumberIsRequired`)),
    icoNumber: Yup.string().strip(),
    labelSettings: Yup.object().strip(),
  })

const createBnlDataSchema = (t: TFunction, addressValidationSchema: Yup.Schema<Address>) =>
  createDefaultDataSchema(t, addressValidationSchema).shape({
    labelSettings: createOptionalLabelSchema(),
  })

const createPplCzDataSchema = (t: TFunction, addressValidationSchema: Yup.Schema<Address>) =>
  createDefaultDataSchema(t, addressValidationSchema).shape({
    chamberOfCommerce: Yup.string().strip(),
    icoNumber: createIcoSchema(t),
  })

export const createOrganisationValidationSchema = (t: TFunction, addressValidationSchema: Yup.Schema<Address>) =>
  Yup.object()
    .shape({
      name: createCompanyNameValidationSchema(t).required(t(`${labelPrefix}.companyIsRequired`)),
      businessUnitCode: Yup.string()
        .default('')
        .required(t(`${labelPrefix}.businessUnitCodeIsRequired`)),
      countryCode: Yup.string()
        .matches(/^[aA-zZ\s]+$/, `${labelPrefix}.countryCodeValidation`)
        .min(2)
        .max(2),
      ccc: Yup.object().when('businessUnitCode', {
        is: isCccBusinessUnit,
        then: () => createCccSchema(t),
      }),
      data: Yup.object().when(['businessUnitCode'], ([businessUnitCode]: [BusinessUnitCode]) => {
        // BNL
        if (isBeNlBusinessUnit(businessUnitCode)) {
          return createBnlDataSchema(t, addressValidationSchema)
        }

        // PPL-CZ
        if (isPplCzBusinessUnit(businessUnitCode)) {
          return createPplCzDataSchema(t, addressValidationSchema)
        }

        // Default
        return createDefaultDataSchema(t, addressValidationSchema)
      }),
      useCompanyAddressBook: Yup.boolean().default(false),
      useSharedShipments: Yup.boolean().default(false),
      useAdvancedParcelType: Yup.boolean().default(false),
      useSharedTickets: Yup.boolean().default(false),
      useSharedImportSchemas: Yup.boolean().default(false),
    })
    .defined()
